import {
  Button,
  Col,
  Form,
  Input,
  Layout,
  message,
  Modal,
  Row,
  Select,
} from "antd";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useWatch } from "antd/es/form/Form";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import ConfirmAction from "../../Components/ConfirmAction/ConfirmAction";
import { formatString } from "../../Utils/Common";
import { parseRoleList } from "../../Utils/auth";
import CustomTable from "../../Components/Table/CustomTable";
import { getRequest, postRequest } from "../../Utils/ApiCalls";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";
import { useTableData } from "../../Utils/useTableData";

const Option = Select;

/** Roles that typically need a branch assignment (non-admin / non-super-admin). */
const BRANCH_REQUIRED_ROLE_IDS = new Set([
  "2", // cashier
  "3", // accounting
  "4", // supervisor
  "5", // custodian
  "9", // accounting_ap
  "10", // treasury_in_charge
  "13", // treasury_assistant
]);

function toRoleIdArray(value: any): string[] {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (value == null || value === "") return [];
  return parseRoleList(String(value)).length
    ? String(value)
        .split(/[,|;]/)
        .map((v) => v.trim())
        .filter(Boolean)
    : [String(value)];
}

const User = () => {
  const [loading, setLoading] = useState("");
  const [showModal, setShowModal] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [form] = Form.useForm();
  const [selectedRow, setSelectedRow]: any = useState({});
  const [branches, setBranches]: any = useState([]);
  const [roles, setRoles]: any = useState([]);
  const [departments, setDepartments]: any = useState([]);
  const roleIds = useWatch("role_id", form) || [];

  const needsBranch = (Array.isArray(roleIds) ? roleIds : toRoleIdArray(roleIds)).some(
    (id: string | number) => BRANCH_REQUIRED_ROLE_IDS.has(String(id))
  );

  const {
    dataSource,
    dataSize,
    loading: tableLoading,
    filterConfig,
    setFilterConfig,
    refetch: fetchTableData,
    handleChangePage,
    handlePageSizeChange,
  } = useTableData<any>({
    endpoint: "users/get",
    initialFilters: { role_id: "", branch_id: "", department_id: "" },
    isFormData: true,
  });

  function clearFields() {
    setShowPassword(false);
    setSelectedRow({});
    form.resetFields();
  }

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Assigned Branch",
      dataIndex: "branch_name",
      key: "branch_name",
    },
    {
      title: "Department",
      dataIndex: "department_name",
      key: "department_name",
      render: (value: any, record: any) =>
        value || record.department || "-",
    },
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
    },
    {
      title: "Roles",
      dataIndex: "role_name",
      key: "role_name",
      render: (value: any, record: any) => {
        if (Array.isArray(record.role_names)) {
          return record.role_names.join(", ");
        }
        return value || "-";
      },
    },
  ];

  function handleFilterChange(value: any, field: string) {
    setFilterConfig((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  }

  async function submitForm(data: any) {
    setLoading("submit");
    const selectedRoleIds = toRoleIdArray(data.role_id);
    const formattedData = {
      ...data,
      user_id: selectedRow.id,
      role_id: selectedRoleIds.join(","),
      department_id: data.department_id,
      branch_id: needsBranch ? data.branch_id : "",
    };
    const response = await postRequest(
      `users/${showModal}`,
      { ...formattedData },
      true
    );

    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => {
        clearFields();
        setShowModal("");
        setLoading("");
        fetchTableData();
      }, 1000);
    } else {
      setLoading("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          `Failed to ${showModal} user.`
      );
    }
  }

  async function deleteUser() {
    const response = await postRequest(
      `users/delete`,
      {
        user_id: selectedRow.id,
      },
      true
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => {
        setShowModal("");
        clearFields();
        fetchTableData();
      }, 1000);
    } else {
      message.error(
        formatString(
          response?.data?.response?.data?.messages?.error_messages[0] ??
            "Failed to delete user."
        )
      );
    }
  }

  async function fetchRoles() {
    setRoles([]);
    const response = await getRequest(`roles/get`, {}, true);
    if (response.success) {
      setRoles(response.data.data);
    }
  }

  async function fetchBranches() {
    setBranches([]);
    const response = await getRequest(`branches/get`, {}, true);

    if (response.success) {
      const mappedBranches = response.data.data.map((branch: any) => ({
        ...branch,
        branch_id: branch.id,
        branch_name: branch.name,
      }));
      setBranches(mappedBranches);
    }
  }

  async function fetchDepartments() {
    setDepartments([]);
    const response = await getRequest(`departments/get`, {}, true);
    if (response.success) {
      setDepartments(response.data.data || []);
    }
  }

  useEffect(() => {
    fetchBranches();
    fetchRoles();
    fetchDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout className="page">
      <Helmet>
        <title>Agua Lab ERP System</title>
      </Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title="Users"
              withDateRangeFilter={false}
              withAddButton={true}
              addBtnText="User"
              onCreate={() => setShowModal("create")}
              withSearchBtn={true}
              onSearch={(value: any) =>
                setFilterConfig((prev: any) => ({
                  ...prev,
                  search: value,
                }))
              }
            />
          </Col>
          <Col xs={24} className="mb-1">
            <span>
              <strong>Filter By:</strong>
            </span>
          </Col>
          <Col span={24} className="mb-3">
            <Row>
              <Col xs={24} md={12} lg={4} className="mb-2 me-2 mb-lg-0">
                <Select
                  className="w-full"
                  value={filterConfig.role_id || ""}
                  onChange={(value) => handleFilterChange(value, "role_id")}
                  showSearch
                  allowClear
                  filterOption={(input: string, option?: any) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  <Option value="">All Roles</Option>
                  {roles.map((role: any) => (
                    <Option key={role.id} value={role.id}>
                      {role.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} md={12} lg={4} className="mb-2 me-2 mb-lg-0">
                <Select
                  className="w-full"
                  value={filterConfig.department_id || ""}
                  onChange={(value) =>
                    handleFilterChange(value, "department_id")
                  }
                  showSearch
                  allowClear
                  filterOption={(input: string, option?: any) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  <Option value="">All Departments</Option>
                  {departments.map((dept: any) => (
                    <Option key={dept.id} value={dept.id}>
                      {dept.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} md={12} lg={4} className="mb-2 me-2 mb-lg-0">
                <Select
                  className="w-full"
                  value={filterConfig.branch_id || ""}
                  onChange={(value) => handleFilterChange(value, "branch_id")}
                  showSearch
                  allowClear
                  filterOption={(input: string, option?: any) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  <Option value="">All Branches</Option>
                  {branches.map((branch: any) => (
                    <Option key={branch.id} value={branch.id}>
                      {branch.name}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </Col>
          <Col span={24}>
            <div className="table-container overflow-auto">
              <div className="min-w-[800px]">
                <CustomTable
                  data={dataSource}
                  columns={columns}
                  rowKey="id"
                  loading={tableLoading}
                  size="small"
                  pagination={{
                    current: filterConfig.page,
                    total: dataSize,
                    showSizeChanger: true,
                    onChange: (page: number) => handleChangePage(page),
                    onShowSizeChange: (_: number, size: number) =>
                      handlePageSizeChange(size),
                  }}
                  handleRowClick={(record: any) => {
                    setShowModal("update");
                    const data = {
                      ...record,
                      role_id: toRoleIdArray(record.role_id),
                      department_id:
                        record.department_id != null
                          ? Number(record.department_id) || record.department_id
                          : undefined,
                    };
                    setSelectedRow(record);
                    form.setFieldsValue(data);
                  }}
                />
              </div>
            </div>
          </Col>
        </Row>
      </Layout>
      <Modal
        open={showModal === "create" || showModal === "update"}
        title={`${showModal === "create" ? "Add" : "Edit"} User`}
        centered
        footer={null}
        onCancel={() => {
          setShowModal("");
          clearFields();
        }}
        width={800}
        maskClosable={false}
      >
        <div className="max-h-[70vh] overflow-y-auto pr-2">
          <Form form={form} onFinish={submitForm} layout="vertical">
            <Row className="w-full ps-3" gutter={[10, 10]}>
              <Col xs={24} lg={12}>
                <Form.Item
                  label="Name"
                  name="name"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: "This field is required.",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} lg={12}>
                <Form.Item
                  label="Username"
                  name="username"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: "This field is required.",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} lg={12}>
                <Form.Item
                  label="Roles"
                  name="role_id"
                  rules={[
                    {
                      required: true,
                      message: "Please select at least one role.",
                    },
                  ]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Select role(s)"
                    showSearch
                    allowClear
                    optionFilterProp="children"
                    filterOption={(input: string, option?: any) =>
                      option?.children
                        ?.toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {roles.map((role: any) => (
                      <Option key={role.id} value={String(role.id)}>
                        {role.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} lg={12}>
                <Form.Item
                  label="Department"
                  name="department_id"
                  rules={[
                    {
                      required: true,
                      message: "This field is required.",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select department"
                    showSearch
                    allowClear
                    filterOption={(input: string, option?: any) =>
                      option?.children
                        ?.toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {departments.map((dept: any) => (
                      <Option key={dept.id} value={dept.id}>
                        {dept.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              {needsBranch && (
                <Col xs={24} lg={12}>
                  <Form.Item
                    label="Branch"
                    name="branch_id"
                    rules={[
                      {
                        required: true,
                        message: "This field is required.",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select branch"
                      showSearch
                      allowClear
                      filterOption={(input: string, option?: any) =>
                        option?.children
                          ?.toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {branches.map((branch: any) => (
                        <Option
                          key={branch.branch_id}
                          value={branch.branch_id}
                        >
                          {branch.branch_name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              )}
              {showModal === "update" && !showPassword && (
                <Row className="w-full">
                  <Col xs={24} lg={12}>
                    <Button
                      type="primary"
                      onClick={() => setShowPassword(true)}
                    >
                      Reset Password
                    </Button>
                  </Col>
                </Row>
              )}
              {(showModal === "create" || showPassword) && (
                <>
                  <Col xs={24} lg={12}>
                    <Form.Item
                      label="Password"
                      name="password"
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: "This field is required.",
                        },
                      ]}
                    >
                      <Input.Password />
                    </Form.Item>
                  </Col>
                  <Col xs={24} lg={12}>
                    <Form.Item
                      label="Confirm Password"
                      name="confirm_password"
                      dependencies={["password"]}
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: "This field is required.",
                        },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue("password") === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error("The two passwords do not match!")
                            );
                          },
                        }),
                      ]}
                    >
                      <Input.Password />
                    </Form.Item>
                  </Col>
                </>
              )}
              {showModal === "update" && showPassword && (
                <Col xs={24} lg={12}>
                  <Button
                    type="primary"
                    onClick={() => setShowPassword(false)}
                  >
                    Cancel Reset Password
                  </Button>
                </Col>
              )}
              <Col lg={24} className="flex justify-end gap-3">
                <Form.Item>
                  <Button
                    onClick={() => {
                      setShowModal("");
                      clearFields();
                    }}
                  >
                    Cancel
                  </Button>
                </Form.Item>
                {showModal === "update" && (
                  <Form.Item>
                    <ConfirmAction
                      title="Are you sure you want to delete this user?"
                      onConfirm={() => deleteUser()}
                    >
                      <Button danger>Delete</Button>
                    </ConfirmAction>
                  </Form.Item>
                )}
                <Form.Item>
                  <Button
                    htmlType="submit"
                    type="primary"
                    loading={loading === "submit"}
                  >
                    Submit
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
      </Modal>
      <PersistentFooter />
    </Layout>
  );
};

export default User;
